import { useState } from 'react';
import type { InputSpec } from '@guess/engine';
import type { AnswerProps } from './AnswerRenderer';
import s from './input.module.scss';

type NumberSpec = Extract<InputSpec, { kind: 'number' }>;

/**
 * L'estimation chiffree.
 *
 * Un curseur, pas un champ de saisie, et c'est un choix de jeu : ce mini-jeu
 * accorde du credit partiel selon l'ecart, donc « a peu pres » rapporte deja.
 * Un curseur invite a approcher, un champ vide invite a chercher la valeur
 * exacte — et a ne rien tenter quand on ne la connait pas.
 *
 * Les bornes, le pas et l'unite viennent tous du serveur. Un mini-jeu
 * « devine le prix » n'aurait donc rien a ajouter ici : il enverrait
 * simplement d'autres bornes et l'unite « EUR ».
 */
export function NumberAnswer({ spec, disabled, onSubmit }: AnswerProps<NumberSpec>) {
  const middle = Math.round((spec.min + spec.max) / 2 / spec.step) * spec.step;
  const [value, setValue] = useState(middle);

  const clamp = (next: number) => Math.min(spec.max, Math.max(spec.min, next));
  const ratio = (value - spec.min) / Math.max(1, spec.max - spec.min);

  return (
    <div className={s.numberStage}>
      <output className={s.numberValue}>
        {value}
        {spec.unit ? <span className={s.numberUnit}>{spec.unit}</span> : null}
      </output>

      <div className={s.numberTrack}>
        {/* Une trace remplie derriere le curseur : elle situe la valeur dans
            la fourchette d'un coup d'oeil, ce qu'un curseur nu ne fait pas. */}
        <span className={s.numberFill} style={{ transform: `scaleX(${ratio})` }} />
        <input
          className={s.numberRange}
          type="range"
          min={spec.min}
          max={spec.max}
          step={spec.step}
          value={value}
          disabled={disabled}
          onChange={(event) => setValue(clamp(Number(event.target.value)))}
          aria-label="Ton estimation"
        />
      </div>

      <div className={s.numberBounds} aria-hidden="true">
        <span>{spec.min}</span>
        <span>{spec.max}</span>
      </div>

      <div className={s.numberNudge}>
        <button
          type="button"
          className={s.nudge}
          disabled={disabled || value <= spec.min}
          onClick={() => setValue((v) => clamp(v - spec.step))}
          aria-label="Diminuer"
        >
          -
        </button>
        <button
          type="button"
          className={s.submit}
          disabled={disabled}
          onClick={() => onSubmit({ kind: 'number', value })}
        >
          Valider
        </button>
        <button
          type="button"
          className={s.nudge}
          disabled={disabled || value >= spec.max}
          onClick={() => setValue((v) => clamp(v + spec.step))}
          aria-label="Augmenter"
        >
          +
        </button>
      </div>
    </div>
  );
}
